export const BRAND_YELLOW = "#F5C518";
export const ENDPOINT = "https://api.jobego.co/mcp";
export const SKILLS_REPO = "https://github.com/selego/jobego/tree/main/skills";

export const SETUP_STEPS = [
  "Open Settings → Connectors in Claude.ai",
  'Click "Add custom connector"',
  "Paste the endpoint URL above",
  "Authorize access to your Jobego account",
  "Done — Claude can now work with your data",
];

export const PROMPTS = [
  {
    name: "review_candidate",
    description:
      "Review a candidate's application: fetch details, profile, and job requirements, then present a structured evaluation with match score, strengths, concerns, and next steps.",
    params: ["application_id"],
  },
  {
    name: "pipeline_overview",
    description:
      "Full pipeline overview for a job: stage funnel with counts, conversion rates, top candidates by match score, and actionable recommendations.",
    params: ["job_id", "organization_id"],
  },
  {
    name: "draft_message",
    description:
      "Draft a personalized message to a candidate based on their application, using existing email templates as inspiration.",
    params: ["application_id", "message_intent", "custom_instructions?"],
  },
];

export const SKILLS = [
  {
    name: "jobego",
    title: "Jobego coach",
    status: "available",
    description:
      "Coach Claude on the Jobego MCP — tool catalog, status taxonomy, and common query patterns for recruitment workflows.",
    details:
      "Loaded automatically the first time Claude touches Jobego data. Teaches the model your status names, how stages map to outcomes, and which tool to reach for in common questions. Read-only.",
  },
  {
    name: "jobego-report",
    title: "Reports",
    status: "available",
    description:
      "Generate structured recruitment reports: activity summaries (period-based) and pipeline health checks, with recommended actions.",
    details:
      "Triggers on phrasings like 'weekly report', 'monthly review', or 'pipeline health'. Outputs markdown with funnel tables, conversion rates, top candidates, and a closing list of recommended actions. Read-only — never mutates data.",
  },
  {
    name: "jobego-screener",
    title: "Candidate screener",
    status: "coming_soon",
    description:
      "Match a candidate to a job — strengths, concerns, and recommended next step.",
    details:
      "Fetches the application and job, scores the candidate across requirements (skills, seniority, location, language), and returns a structured evaluation: match score, top 3 strengths, top 3 concerns, and a recommended next step. Built to be chained — 'screen these 12 candidates and rank them' produces a comparable batch in one go.",
  },
  {
    name: "jobego-outreach",
    title: "Outreach drafter",
    status: "coming_soon",
    description:
      "Draft personalized candidate messages — cold approach, interview invite, rejection, follow-up.",
    details:
      "Given an application and an intent (cold approach, interview invite, rejection, follow-up), picks the right email template, hydrates it with candidate and job context, and produces 2–3 tone-calibrated variants. Pairs naturally with send_message once you pick one.",
  },
  {
    name: "jobego-pipeline-cleanup",
    title: "Pipeline cleanup",
    status: "coming_soon",
    description:
      "Triage stale work — find applications stuck, candidates ghosted, jobs with no movement.",
    details:
      "Surfaces applications stuck in interview stages beyond a threshold, candidates with no recent touch, and jobs with zero movement this week. Output is a prioritized punch list ('move these 4 to rejected', 'follow up with these 6', 'archive this job'). Read-only — the recruiter confirms each action.",
  },
  {
    name: "jobego-job-writer",
    title: "Job ad writer",
    status: "coming_soon",
    description:
      "Draft a job ad from a one-line brief — voice, structure, and culture pulled from your org.",
    details:
      "From a short brief, reads your org's existing job ads to mirror voice and structure, pulls culture/values from the organisation, and outputs a complete spec (title, mission, requirements, perks) ready to refine and publish. Solves the cold-start problem when a recruiter opens 'new job'.",
  },
  {
    name: "jobego-interview-prep",
    title: "Interview kit",
    status: "coming_soon",
    description:
      "Generate an interview kit for a given application — summary, tailored questions, eval grid.",
    details:
      "For a given application, produces a 1-paragraph candidate summary, 5–7 tailored questions targeting gaps between the CV and the job requirements, and an evaluation grid with weighted criteria. The recruiter walks into the meeting prepared instead of re-reading the CV cold.",
  },
];

export const ROUTES = [
  {
    group: "Account",
    tools: [
      { name: "whoami", description: "Identify the authenticated user" },
      { name: "list_my_organizations", description: "List organisations you belong to" },
      { name: "search_team_members", description: "Find recruiters or agency users in an org" },
    ],
  },
  {
    group: "Jobs",
    tools: [
      { name: "search_jobs", description: "Search and filter job ads" },
      { name: "get_job", description: "Full details of a single job" },
      { name: "get_job_stages", description: "Pipeline stages configured on a job" },
      { name: "get_job_stats", description: "Applications by status and source for a job" },
    ],
  },
  {
    group: "Applications",
    tools: [
      { name: "search_applications", description: "Search candidates' applications" },
      { name: "get_application", description: "Full application + candidate profile" },
      { name: "move_application", description: "Change an application's pipeline stage" },
      { name: "get_pipeline_summary", description: "Funnel view with counts per stage" },
    ],
  },
  {
    group: "Candidates",
    tools: [
      { name: "search_candidates", description: "Search your talent pool" },
      { name: "get_candidate", description: "Full profile + application history" },
    ],
  },
  {
    group: "Conversations",
    tools: [
      { name: "search_conversations", description: "Search message threads" },
      { name: "get_conversation_messages", description: "Read messages in a thread" },
      { name: "send_message", description: "Post a text message as the authenticated user" },
    ],
  },
  {
    group: "Organization",
    tools: [
      { name: "get_organization", description: "Organisation details and config" },
      { name: "get_organization_stats", description: "Top-level recruitment stats" },
    ],
  },
  {
    group: "Channels",
    tools: [
      { name: "search_channels", description: "List distribution channels" },
      { name: "get_channel_posts", description: "Posts published on a channel" },
    ],
  },
];
